import { AssessmentOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { CardContainer, Button } from 'finara-components';
import { useState } from 'react';

import { Navbar as AdminNavbar } from '@/components/admin/general/Navbar';
import { Navbar as SuperAdminNavbar } from '@/components/super-admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { riskCategories } from '@/data/riskAssessment';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { useAuth } from '@/hooks/useAuth';
import type { RiskAssessment } from '@/types/riskAssessment';
import {
  calculateAssessmentResult,
  getRiskLevelColor,
  getRiskLevelLabel,
} from '@/utils/riskCalculations';

import { MitigationRecommendations } from './components/MitigationRecommendations';
import { RiskCategoryCard } from './components/RiskCategoryCard';
import { RiskMatrix } from './components/RiskMatrix';

export default function RiskAssessmentPage() {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState<RiskAssessment[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Determine which navbar to use based on user role
  const isSuperAdmin = user?.role === 'super_admin';
  const navItems = isSuperAdmin ? superAdminNavItems : adminNavItems;

  // Add print styles to head
  if (typeof document !== 'undefined') {
    const styleId = 'risk-assessment-print-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
          @page {
            margin: 1cm;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  const handleFactorChange = (
    categoryId: string,
    factorId: string,
    optionId: string,
    riskLevel: 'low' | 'medium' | 'high',
    score: number
  ) => {
    setAssessments(prev => {
      const filtered = prev.filter(a => !(a.categoryId === categoryId && a.factorId === factorId));
      return [
        ...filtered,
        {
          categoryId,
          factorId,
          selectedOptionId: optionId,
          riskLevel,
          score,
        },
      ];
    });
    setShowResults(false);
  };

  const handleCalculateRisk = () => {
    // Check if all factors have been assessed
    const totalFactors = riskCategories.reduce((sum, category) => sum + category.factors.length, 0);

    if (assessments.length < totalFactors) {
      alert('Por favor, complete todos los factores de riesgo antes de calcular.');
      return;
    }

    setShowResults(true);
  };

  const handleReset = () => {
    setAssessments([]);
    setShowResults(false);
  };

  const result = showResults ? calculateAssessmentResult(assessments, riskCategories) : null;

  const progress = riskCategories.reduce((sum, category) => {
    const categoryAssessments = assessments.filter(a => a.categoryId === category.id);
    return sum + categoryAssessments.length;
  }, 0);

  const totalFactors = riskCategories.reduce((sum, category) => sum + category.factors.length, 0);

  const progressPercentage = (progress / totalFactors) * 100;

  return (
    <div className="min-h-screen">
      <div className="no-print">
        {isSuperAdmin ? (
          <SuperAdminNavbar
            navItems={navItems}
            userName={user.name || ''}
            activeItem="Reportes & Datos"
          />
        ) : (
          <AdminNavbar
            navItems={navItems}
            userName={user?.name || ''}
            activeItem="Reportes & Datos"
            pageTitle="Evaluación de Riesgos"
          />
        )}
      </div>

      <div className="px-4 py-8 min-h-screen">
          {/* Header */}
          <div className="mb-8 no-print">
            <div className="flex items-center gap-3 mb-2">
              <AssessmentOutlined className="text-brand-white text-3xl" />
              <h1 className="text-3xl font-bold text-brand-white">Evaluación de Riesgos (EBR)</h1>
            </div>
            <p className="text-muted">
              Herramienta interactiva para evaluar riesgos según el Enfoque Basado en Riesgos (EBR)
              para prevención de lavado de dinero y financiamiento al terrorismo.
            </p>
          </div>

          {/* Progress Bar */}
          <CardContainer className="mb-6 no-print">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted">Progreso de la Evaluación</span>
                <span className="text-sm font-medium text-muted">
                  {progress} / {totalFactors} factores completados
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div
                  className="bg-brand-blue h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </CardContainer>

          {/* Instructions */}
          {!showResults && (
            <CardContainer className="mb-6 no-print">
              <div className="p-6 bg-blue-500/10 border-l-4 border-brand-blue">
                <div className="flex items-start gap-3">
                  <WarningAmberOutlined className="text-brand-blue mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-brand-white mb-1">Instrucciones</h3>
                    <p className="text-sm text-muted">
                      Complete todos los factores de riesgo seleccionando la opción que mejor
                      describa su situación. Una vez completada la evaluación, haga clic en
                      &quot;Calcular Nivel de Riesgo&quot; para obtener el resultado y
                      recomendaciones de mitigación.
                    </p>
                  </div>
                </div>
              </div>
            </CardContainer>
          )}

          {/* Risk Categories */}
          {!showResults && (
            <div className="space-y-6">
              {riskCategories.map(category => (
                <RiskCategoryCard
                  key={category.id}
                  category={category}
                  assessments={assessments.filter(a => a.categoryId === category.id)}
                  onFactorChange={handleFactorChange}
                />
              ))}
            </div>
          )}

          {/* Action Buttons */}
          {!showResults && (
            <div className="mt-8 flex gap-4 justify-center no-print">
              <Button
                type="button"
                onClick={handleReset}
                size="large"
                disabled={assessments.length === 0}
                variant="white"
              >
                Limpiar Evaluación
              </Button>
              <Button
                type="button"
                onClick={handleCalculateRisk}
                size="large"
                disabled={progress < totalFactors}
                variant="black"
              >
                Calcular Nivel de Riesgo
              </Button>
            </div>
          )}

          {/* Results */}
          {showResults && result && (
            <div className="space-y-6">
              {/* Overall Risk Level */}
              <CardContainer>
                <div className="p-8">
                  <h2 className="text-2xl font-bold text-brand-white mb-6 text-center">
                    Resultado de la Evaluación
                  </h2>
                  <div className="flex flex-col items-center">
                    <div
                      className={`px-8 py-4 rounded-xl border-2 text-center ${getRiskLevelColor(result.overallRiskLevel)}`}
                    >
                      <div className="text-sm font-medium mb-1">Nivel de Riesgo Global</div>
                      <div className="text-4xl font-bold">
                        {getRiskLevelLabel(result.overallRiskLevel)}
                      </div>
                    </div>
                    <div className="mt-4 text-muted text-center">
                      Puntuación: {result.totalScore} / {result.maxScore}
                    </div>
                  </div>
                </div>
              </CardContainer>

              {/* Risk Matrix */}
              <RiskMatrix categoryScores={result.categoryScores} />

              {/* Mitigation Recommendations */}
              <MitigationRecommendations
                mitigations={result.mitigations}
                overallRiskLevel={result.overallRiskLevel}
              />

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center no-print">
                <Button type="button" onClick={handleReset} size="large" variant="white">
                  Nueva Evaluación
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    window.print();
                  }}
                  size="large"
                  variant="black"
                >
                  Exportar a PDF
                </Button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
